import { useState, useEffect, useMemo } from "react";
import {
  IconButton,
  Chip,
  Checkbox,
  Grid,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Typography,
  Button,
} from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import { columnTextField, columnPriceField } from "utils/tables-utils";
import MaskAndValid from "utils/maskAndValid";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import { InfoOutlined, ExpandMore, ExpandLess } from "@mui/icons-material";

export default function TableGestaoCobranca({
  data,
  totalRegistros,
  page,
  sizePerPage,
  onTableChange,
  onAction,
  isClient,
  dateReference,
}) {
  const [dataTable, setDataTable] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const snackbar = useSnackbar();

  useEffect(() => {
    setDataTable(
      data.map((item) => ({
        ...item,
        selected: false,
      }))
    );
    setSelectAll(false);
    setExpandedRows([]);
  }, [data]);

  const handleSelectRow = (id) => {
    setDataTable((prevData) =>
      prevData.map((item) =>
        item.IdPessoaLoja === id ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleSelectAll = () => {
    setDataTable((prevData) =>
      prevData.map((item) => ({
        ...item,
        selected: !selectAll,
      }))
    );
    setSelectAll(!selectAll);
  };

  const handleApproveCharges = async () => {
    const selectedRows = dataTable.filter((item) => item.selected);
    console.log(selectedRows);
    const hasApprovedRows = selectedRows.some(
      (row) => row.Status === "Aprovado"
    );

    if (hasApprovedRows) {
      snackbar.showMessage(
        "Não é possível aprovar cobranças que já foram aprovadas. Selecione apenas cobranças pendentes.",
        { severity: "error" }
      );
      return;
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const hasCurrentOrFutureMonth = selectedRows.some((row) => {
      return row.Cobrancas?.some((cobranca) => {
        const dataReferencia = new Date(cobranca.DataReferencia);
        const refYear = dataReferencia.getFullYear();
        const refMonth = dataReferencia.getMonth();

        if (refYear > currentYear) return true;

        if (refYear === currentYear && refMonth >= currentMonth) return true;

        return false;
      });
    });

    if (hasCurrentOrFutureMonth) {
      snackbar.showMessage("Selecione apenas cobranças de meses fechados.", {
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);
      await Api.put(
        "approve-closed-charges",
        "franquias",
        {
          idPessoaLoja: selectedRows.map((row) => row.IdPessoaLoja).join(","),
          dataReferencia: dateReference,
        },
        true
      );
      snackbar.showMessage("Cobranças aprovadas com sucesso", {
        severity: "success",
      });
      onTableChange("page", { page: 0, sizePerPage: 25 });

    } catch (error) {
      snackbar.showMessage("Erro ao aprovar cobranças: " + error.message, {
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const columnModel = [
    !isClient && {
      dataField: "selected",
      text: "",
      align: "center",
      headerAlign: "center",
      formatter: (_, row) => (
        <Checkbox
          checked={row.selected}
          onChange={(e) => {
            e.stopPropagation();
            handleSelectRow(row.IdPessoaLoja);
          }}
          onClick={(e) => e.stopPropagation()}
          color="primary"
        />
      ),
      headerFormatter: () => (
        <Checkbox
          checked={selectAll}
          onChange={handleSelectAll}
          color="primary"
        />
      ),
      style: {
        width: "5vw",
      },
    },
    columnTextField({
      dataField: "NomeLoja",
      text: "Loja/Serviço/NF",
      headerAlign: "left",
      align: "left",
      style: {
        width: "50vw",
      },
    }),
    columnPriceField({
      dataField: "Base",
      text: "Base (sell-out)",
      align: "center",
      style: {
        width: "10vw",
      },
    }),
    columnTextField({
      dataField: "Status",
      text: "Status",
      align: "center",
      formatter: (_, row) => {
        const status = row.Status === "Aprovado" ? true : false;
        return (
          <Chip
            label={row.Status}
            size="small"
            style={{
              width: "110px",
              padding: "1rem 0.5rem",
              backgroundColor: status
                ? "rgba(0, 128, 0, 0.1)"
                : "rgba(255, 215, 0, 0.1)",
              border: status
                ? "1px solid rgba(0, 128, 0, 0.5)"
                : "1px solid rgba(255, 215, 0, 0.5)",
            }}
          />
        );
      },
      style: {
        width: "15vw",
      },
    }),
    columnPriceField({
      dataField: "Valor",
      text: "Valor a ser cobrado",
      align: "center",
      style: {
        width: "10vw",
      },
    }),
    columnTextField({
      dataField: "Ações",
      text: "",
      align: "center",
      formatter: (_, row) => {
        return (
          <IconButton
            color="primary"
            onClick={() => onAction(row)}
            size="small"
            style={{ cursor: "pointer" }}
          >
            <InfoOutlined fontSize="small" color="textPrimary" />
          </IconButton>
        );
      },
      style: {
        width: "5vw",
      },
    }),
  ];

  const expandRow = useMemo(
    () => ({
      expanded: expandedRows,
      nonExpandable: [],
      onlyOneExpanding: true,
      renderer: (row) => (
        <Grid item xs={12}>
          {row?.Cobrancas?.length > 0 ? (
            <Table>
              <TableBody style={{ border: "1px solid #e0e0e0" }}>
                {row?.Cobrancas?.map((cobranca) => (
                  <TableRow key={cobranca.IdFranquiaCobranca || cobranca.Id}>
                    <TableCell style={{ width: "10vw" }}>{""}</TableCell>
                    <TableCell style={{ width: "50vw" }}>
                      {cobranca.NomeTipoCobranca}
                    </TableCell>
                    <TableCell style={{ width: "35vw", textAlign: "center" }}>
                      {`R$ ${MaskAndValid.maskPrice(cobranca.ValorBase)}`}
                    </TableCell>
                    <TableCell style={{ width: "15vw" }}>{""}</TableCell>
                    <TableCell style={{ width: "10vw", textAlign: "center" }}>
                      {`R$ ${MaskAndValid.maskPrice(cobranca.ValorCobrado)}`}
                    </TableCell>
                    <TableCell style={{ width: "4vw" }}> </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div
              style={{ padding: "1rem", textAlign: "center", color: "#999" }}
            >
              Nenhuma regra de cobrança encontrada
            </div>
          )}
        </Grid>
      ),
      onExpand: (row, isExpanded) => {
        if (isExpanded) {
          setExpandedRows([row.IdPessoaLoja]);
        } else {
          setExpandedRows([]);
        }
      },
      showExpandColumn: true,
      expandHeaderColumnRenderer: () => (
        <div
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {" "}
        </div>
      ),
      expandColumnRenderer: ({ expanded }) => {
        if (expanded) {
          return (
            <ExpandLess
              fontSize="small"
              color="textPrimary"
              style={{ cursor: "pointer" }}
            />
          );
        }
        return (
          <ExpandMore
            fontSize="small"
            color="textPrimary"
            style={{ cursor: "pointer" }}
          />
        );
      },
    }),
    [expandedRows]
  );

  return (
    <ToolkitTable
      dataTable={dataTable}
      searched={Boolean(dataTable)}
      keyField={"IdPessoaLoja"}
      columnModel={columnModel}
      notFoundTitle="Sua pesquisa não encontrou nenhum resultado correspondente."
      pagination={{
        options: [25, 50, 100],
        totalSize: totalRegistros,
        page: page + 1,
        sizePerPage: sizePerPage,
        showTotal: true,
      }}
      bootstrapTableProps={{
        remote: true,
        onTableChange: onTableChange,
        expandRow: expandRow,
      }}
      CustomHeader={() => {
        const hasSelectedRows = dataTable.some((item) => item.selected);

        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            className="pl-4"
            style={{ height: 60, padding: "0 24px" }}
          >
            <Typography variant="h2">Gestão de Cobrança</Typography>
            {!isClient && (
              <div style={{ display: "flex", gap: "10px" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApproveCharges}
                  style={{ width: "20vw" }}
                  disabled={!hasSelectedRows || loading}
                >
                  Aprovar Cobranças
                </Button>
              </div>
            )}
          </Box>
        );
      }}
    />
  );
}
